import { Injectable, OnDestroy } from '@angular/core';
import {
  AppPartnerService,
  GetAppSettingsRequest,
  GetUpgradeDialogContentResponse,
  PartnerApiService,
  UpgradeDialogContent,
} from '@galaxy/marketplace-apps';
import {
  AppPartnerSettingsService,
  Currency,
  Frequency,
  GetMultiPackageResponse,
  MarketplacePackagesApiService,
  PartnerMarketContainer,
  RetailPrice,
} from '@vendasta/marketplace-packages';
import { DisplayPriceService } from '@vendasta/shared';
import { UpgradeCTADialogConfig } from '@vendasta/uikit';
import { EMPTY, Observable, Subscription, combineLatest, merge, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { VDC_APP_ID } from '../../../globals';
import { AccountsService } from '../../accounts.service';
import { Account, AccountService } from '../account';
import { getImageSrc } from '../image-utilities';
import { Site, SiteService } from '../site';
import {
  customUpgradePathSize,
  findMostFrequentPrice,
  getFrequencyTranslation,
  hasCustomUpgradePrice,
  isCustomUpgrade,
  isPriceContactSales,
  isPriceFree,
  isProUpgrade,
} from './get-pro.utils';

interface UpgradeConfig {
  price: string;
  frequency: string;
  text: string;
}

@Injectable()
export class GetProService implements OnDestroy {
  PRO_CONFIG = new Map<string, UpgradeCTADialogConfig>();
  keySellingPoints = [
    'UPGRADE_CTA.KEY_SELLING_POINTS.CUSTOM_DOMAIN',
    'UPGRADE_CTA.KEY_SELLING_POINTS.BACKUPS',
    'UPGRADE_CTA.KEY_SELLING_POINTS.STAGING',
    'UPGRADE_CTA.KEY_SELLING_POINTS.EXPERIENCE',
  ];
  imageUrl = getImageSrc('images/website-express-CTA-image.png');
  imageText = 'UPGRADE_CTA.IMAGE_TEXT';
  ctaText = 'UPGRADE_CTA.CTA_TEXT';
  upgradeNowText = 'UPGRADE_CTA.UPGRADE_NOW_TEXT';
  activeOrderPrimaryText = 'UPGRADE_CTA.ACTIVE_ORDER_PRIMARY';
  activeOrderSecondaryText = 'UPGRADE_CTA.ACTIVE_ORDER_SECONDARY';
  editionName = 'Pro';
  marketId: string;

  private subscriptions: Subscription[] = [];

  constructor(
    private appPartnerSettingsService: AppPartnerSettingsService,
    private displayPriceService: DisplayPriceService,
    private accountService: AccountService,
    private accountsService: AccountsService,
    private partnerSettingsService: PartnerApiService,
    private marketplacePackagesService: MarketplacePackagesApiService,
    private appPartnerService: AppPartnerService,
    private siteService: SiteService,
  ) {
    const partnerId$ = merge(this.accountService.result(), this.siteService.get()).pipe(
      map((res: Account | Site) => res.partnerId),
    );
    const partnerMarketContainer$ = combineLatest([partnerId$, this.accountsService.marketId$]).pipe(
      map(([partnerId, marketId]: [string, string]) => {
        return new PartnerMarketContainer({
          partnerId: partnerId,
          marketId: marketId,
        });
      }),
    );

    const appSettings$ = partnerMarketContainer$.pipe(
      switchMap((pmConfig: PartnerMarketContainer) => {
        const req: GetAppSettingsRequest = new GetAppSettingsRequest({
          partnerId: pmConfig.partnerId,
          marketId: pmConfig.marketId,
          appId: VDC_APP_ID,
        });
        return this.partnerSettingsService.getAppSettings(req);
      }),
      catchError(() => of(null)),
    );

    const upgradePrice$: Observable<RetailPrice> = combineLatest([appSettings$, partnerMarketContainer$]).pipe(
      switchMap(([appSettings, pmConfig]) => {
        if (
          !!appSettings &&
          isCustomUpgrade(appSettings.appSettings) &&
          !hasCustomUpgradePrice(appSettings.appSettings)
        ) {
          return of(null);
        }
        const customUpgrade = appSettings ? isCustomUpgrade(appSettings.appSettings) : false;
        const packageId = (appSettings && appSettings.appSettings.editionChange?.priceReferencePackageId) || '';
        if (!customUpgrade || !packageId || packageId === '') {
          const overridePrice$ = this.appPartnerSettingsService
            .getRetailPrice(VDC_APP_ID, pmConfig, null, true)
            .pipe(catchError(() => of(null)));
          const defaultPrice$ = this.appPartnerSettingsService
            .getRetailPrice(VDC_APP_ID, pmConfig, null, false)
            .pipe(catchError(() => of(null)));
          return combineLatest([overridePrice$, defaultPrice$]).pipe(
            map(([overridePrice, defaultPrice]: [RetailPrice, RetailPrice]) => overridePrice || defaultPrice),
          );
        } else {
          return this.marketplacePackagesService.getMultiPackages({ packageIds: [packageId] }).pipe(
            map((resp: GetMultiPackageResponse) => {
              if (!!resp.packageContainers && resp.packageContainers.length > 0) {
                const container = resp.packageContainers[0];
                return findMostFrequentPrice(container.package.pricing);
              }
              return {
                price: -1,
              } as RetailPrice;
            }),
          );
        }
      }),
    );

    const upgradeConfig$: Observable<UpgradeConfig> = combineLatest([appSettings$, upgradePrice$]).pipe(
      switchMap(([appSettings, upgradePrice]) => {
        if (!upgradePrice) {
          return of(null);
        }

        const currencyKey = Currency[upgradePrice.currency || Currency.USD];
        const frequencyKey = upgradePrice.billingFrequency || Frequency.MONTHLY;
        let frequency = getFrequencyTranslation('UPGRADE_CTA.FREQUENCY', frequencyKey);
        return this.displayPriceService
          .formatDisplayPrice(upgradePrice.price, currencyKey, null, true, true, true, false)
          .pipe(
            map((displayPrice: string) => {
              this.upgradeNowText = 'UPGRADE_CTA.UPGRADE_NOW_TEXT';
              if (!appSettings) {
                return <UpgradeConfig>{
                  price: displayPrice,
                  frequency: frequency,
                  text: this.upgradeNowText,
                };
              }
              if (isCustomUpgrade(appSettings.appSettings)) {
                if (customUpgradePathSize(appSettings.appSettings) > 1) {
                  this.upgradeNowText = 'UPGRADE_CTA.UPGRADE_NOW_STARTING_AT';
                }
                if (!hasCustomUpgradePrice(appSettings.appSettings)) {
                  this.upgradeNowText = null;
                }
              }

              if (isProUpgrade(appSettings.appSettings) || isCustomUpgrade(appSettings.appSettings)) {
                if (isPriceFree(upgradePrice)) {
                  frequency = null;
                } else if (isPriceContactSales(upgradePrice)) {
                  this.upgradeNowText = null;
                  frequency = null;
                }
                return <UpgradeConfig>{
                  price: displayPrice,
                  frequency: frequency,
                  text: this.upgradeNowText,
                };
              }
              return { price: '', frequency: null, text: null };
            }),
          );
      }),
    );

    const contentConfig$: Observable<GetUpgradeDialogContentResponse> = partnerMarketContainer$.pipe(
      switchMap((pmContainer: PartnerMarketContainer) => {
        return this.appPartnerService
          .getUpgradeDialogContent(pmContainer.partnerId, pmContainer.marketId, VDC_APP_ID)
          .pipe(catchError((err) => (err.status === 404 ? of(null) : EMPTY)));
      }),
    );

    this.subscriptions.push(
      combineLatest([upgradeConfig$, contentConfig$])
        .pipe(
          map(([upgradeConfig, contentConfig]: [UpgradeConfig, GetUpgradeDialogContentResponse]) => {
            const upgradeContent = contentConfig ? contentConfig.upgradeDialogContent : null;
            this.setConfig(upgradeConfig, upgradeContent);
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  setConfig(upgradeConfig: UpgradeConfig, contentConfig: UpgradeDialogContent): void {
    const config = {
      imageUrl: contentConfig?.imageUrl ?? this.imageUrl,
      imageText: contentConfig?.titleText ?? this.imageText,
      editionName: this.editionName,
      keySellingPoints: contentConfig?.sellingPoints ?? this.keySellingPoints,
      ctaText: contentConfig?.buttonText ?? this.ctaText,
      activeOrderPrimaryText: this.activeOrderPrimaryText,
      activeOrderSecondaryText: this.activeOrderSecondaryText,
      activeOrderImageUrl: this.imageUrl,
    };

    if (contentConfig?.partnerId == 'ZX0I') {
      this.PRO_CONFIG.set('upgrade', config);
    } else {
      this.PRO_CONFIG.set('upgrade', {
        ...config,
        price: upgradeConfig?.price ?? '',
        frequency: upgradeConfig?.frequency ?? null,
        upgradeNowText: upgradeConfig?.text ?? null,
      });
    }
  }
}
